import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Switch,
  Box,
  VStack,
  Spinner,
  Center,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Flex,
  Text,
  IconButton,
  HStack,
  Heading,
  Tooltip,
} from '@chakra-ui/react';
import { MultiSelect } from 'chakra-multiselect';
import { Formik, Field, Form, FieldArray } from 'formik';
import styled from '@emotion/styled';
import { GoChevronDown } from 'react-icons/go';
import { IoMdAdd } from 'react-icons/io';
import { MdClear } from 'react-icons/md';
import { useMemo } from 'react';

import FormErrorMessage from './FormErrorMessage';
import FormWhatsappTemplateSelection from './FormWhatsappTemplateSelection';
import { ChannelType, MessageCampaignType } from '../consts/consts';
import { formFields, createValidationSchema } from '../consts/formConsts';
import { useCampaigns } from '../context/CampaignsContext';
import { useWhatsAppTemplates } from '../../context/WhatsAppTemplatesContext';

const CreateEditCampaignModal = () => {
  const {
    companiesOptions,
    loaders: { isCreateEditLoading: isLoading },
    isEditMode,
    currentEditCampaign,
    isModalOpen,
    onModalClose,
    handleSubmit,
  } = useCampaigns();

  const { whatsappTemplates } = useWhatsAppTemplates();

  // The media rule needs each template's headerType, so the schema is rebuilt when they load
  const validationSchema = useMemo(
    () => createValidationSchema(whatsappTemplates),
    [whatsappTemplates],
  );

  const initialValues = {
    type: isEditMode ? currentEditCampaign?.type || '' : '',
    companies: isEditMode ? currentEditCampaign?.companies || [] : [],
    isActive: isEditMode ? (currentEditCampaign?.isActive ?? false) : true,
    messageSequence: {
      messages: isEditMode
        ? currentEditCampaign?.messageSequence?.messages || []
        : [
            {
              templateKey: '',
              daysAfterPrevious: 0,
              channelType: '',
              params: [],
              mediaUrl: '',
            },
          ],
    },
  };

  return (
    <Modal isOpen={isModalOpen} onClose={onModalClose} isCentered size="xl">
      <ModalOverlay />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => handleSubmit(values)}
        enableReinitialize={true}
      >
        {({ values, setFieldValue }) => {
          const onSwitchChange = (e) => {
            const isChecked = e.target.checked;
            setFieldValue(formFields.isActive.name, isChecked);
          };

          const onMultiSelectChange = (selected) => {
            setFieldValue(formFields.companies.name, selected);
          };

          return (
            <Form>
              <ModalContent maxWidth="900px" maxH="700px">
                <ModalHeader>{isEditMode ? 'עריכת קמפיין' : 'הוספת קמפיינים'}</ModalHeader>
                <StyledModalBody>
                  {isLoading ? (
                    <Center p={6}>
                      <Spinner size="xl" thickness="4px" speed="0.65s" color="teal.500" />
                    </Center>
                  ) : (
                    <>
                      <FormControl display="flex" alignItems="center">
                        <FormLabel mb="0">{formFields.isActive.label}</FormLabel>
                        <Field name={formFields.isActive.name}>
                          {({ field }) => (
                            <Switch
                              {...field}
                              isChecked={values.isActive}
                              onChange={onSwitchChange}
                              colorScheme="teal"
                              mr={2}
                            />
                          )}
                        </Field>
                      </FormControl>

                      <FormControl mt={4}>
                        <FormLabel>{formFields.type.label}</FormLabel>
                        <Field name={formFields.type.name}>
                          {({ field }) => (
                            <StyledSelect
                              {...field}
                              placeholder={formFields.type.placeholder}
                              color={values?.type ? 'inherit' : 'gray.400'}
                              iconColor="black"
                              icon={<GoChevronDown />}
                              isDisabled={isEditMode}
                            >
                              {Object.entries(MessageCampaignType).map(([key, value]) => (
                                <option key={key} value={value}>
                                  {value}
                                </option>
                              ))}
                            </StyledSelect>
                          )}
                        </Field>
                        <FormErrorMessage name={formFields.type.name} />
                      </FormControl>

                      <FormControl mt={0}>
                        <FormLabel>{formFields.companies.label}</FormLabel>
                        <Box
                          pointerEvents={
                            isEditMode && !!values?.companies?.length ? 'none' : 'auto'
                          }
                        >
                          <MultiSelect
                            options={companiesOptions}
                            value={values.companies}
                            onChange={onMultiSelectChange}
                            placeholder={formFields.companies.placeholder}
                            disabled={isEditMode && !!values?.companies?.length}
                          />
                        </Box>
                        <FormErrorMessage name={formFields.companies.name} />
                      </FormControl>

                      <FieldArray name="messageSequence.messages">
                        {({ remove, push }) => (
                          <>
                            <Flex justify="space-between" align="center" mt={4}>
                              <Heading size="md">{formFields.messageSequence.label}</Heading>
                              <Button
                                leftIcon={<IoMdAdd />}
                                onClick={() =>
                                  push({
                                    templateKey: '',
                                    daysAfterPrevious: 0,
                                    channelType: '',
                                    params: [],
                                    mediaUrl: '',
                                  })
                                }
                                colorScheme="teal"
                                variant="outline"
                                size="sm"
                              >
                                הוסף הודעה
                              </Button>
                            </Flex>

                            <Box mt={4}>
                              <VStack spacing={4} align="stretch">
                                {values.messageSequence.messages.length > 0 &&
                                  values.messageSequence.messages.map((_, index) => (
                                    <Box
                                      key={index}
                                      p={4}
                                      borderWidth="1px"
                                      borderRadius="md"
                                      position="relative"
                                    >
                                      <Flex justifyContent="space-between" mb={2}>
                                        <Text fontWeight="bold">הודעה {index + 1}</Text>
                                        <IconButton
                                          icon={<MdClear />}
                                          variant="outline"
                                          size="sm"
                                          onClick={() => remove(index)}
                                          aria-label="מחק הודעה"
                                          isDisabled={values.messageSequence.messages.length === 1}
                                        />
                                      </Flex>

                                      <HStack spacing={4} alignItems="flex-start">
                                        <FormControl>
                                          <FormLabel fontSize="sm">
                                            {formFields.messageSequence.messages.channelType.label}
                                          </FormLabel>
                                          <Field
                                            name={`messageSequence.messages.${index}.channelType`}
                                          >
                                            {({ field }) => (
                                              <StyledSelect
                                                {...field}
                                                placeholder={
                                                  formFields.messageSequence.messages.channelType
                                                    .placeholder
                                                }
                                                icon={<GoChevronDown />}
                                                color={
                                                  values.messageSequence.messages[index].channelType
                                                    ? 'inherit'
                                                    : 'gray.400'
                                                }
                                              >
                                                {Object.entries(ChannelType).map(([key, value]) => (
                                                  <option key={key} value={value}>
                                                    {key}
                                                  </option>
                                                ))}
                                              </StyledSelect>
                                            )}
                                          </Field>
                                          <FormErrorMessage
                                            name={`messageSequence.messages.${index}.channelType`}
                                          />
                                        </FormControl>

                                        <FormControl>
                                          <FormLabel fontSize="sm">
                                            {
                                              formFields.messageSequence.messages.daysAfterPrevious
                                                .label
                                            }
                                          </FormLabel>
                                          <Field
                                            name={`messageSequence.messages.${index}.daysAfterPrevious`}
                                          >
                                            {({ field }) => (
                                              <Tooltip
                                                placement="top"
                                                label="0 ימים - באותו יום, או בחר כמה ימים להמתין עד שליחת ההודעה הבאה"
                                              >
                                                <StyledNumberInput
                                                  {...field}
                                                  onChange={(_, value) =>
                                                    setFieldValue(
                                                      `messageSequence.messages.${index}.daysAfterPrevious`,
                                                      value,
                                                    )
                                                  }
                                                  min={0}
                                                  max={100}
                                                  value={field.value}
                                                  isDisabled={
                                                    values.messageSequence.messages.length === 1
                                                  }
                                                >
                                                  <NumberInputField />
                                                  <NumberInputStepper>
                                                    <NumberIncrementStepper />
                                                    <NumberDecrementStepper />
                                                  </NumberInputStepper>
                                                </StyledNumberInput>
                                              </Tooltip>
                                            )}
                                          </Field>
                                          <FormErrorMessage
                                            name={`messageSequence.messages.${index}.daysAfterPrevious`}
                                          />
                                        </FormControl>
                                      </HStack>

                                      {values.messageSequence.messages[index].channelType ===
                                        String(ChannelType['Whatsapp (Unregistered users)']) && (
                                        <FormWhatsappTemplateSelection messageIndex={index} />
                                      )}
                                    </Box>
                                  ))}
                              </VStack>
                            </Box>
                          </>
                        )}
                      </FieldArray>
                      <FormErrorMessage name="messageSequence.messages" />
                    </>
                  )}
                </StyledModalBody>
                <ModalFooter>
                  <Button type="submit" colorScheme="teal" isLoading={isLoading} ml={3}>
                    אישור
                  </Button>
                  <Button onClick={onModalClose} variant="outline">
                    סגור
                  </Button>
                </ModalFooter>
              </ModalContent>
            </Form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default CreateEditCampaignModal;

// ----------------------------------- Styled Components -----------------------------------
const StyledSelect = styled(Select)`
  padding-inline: 32px 16px;
`;

const StyledNumberInput = styled(NumberInput)`
  input {
    padding-right: 32px;
  }
`;

const StyledModalBody = styled(ModalBody)`
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    width: 10px;
    background: ${({ theme }) => theme.colors.gray[100]};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.gray[300]};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.gray[400]};
  }
`;
